import { NestedTreeControl } from '@angular/cdk/tree';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { Router } from '@angular/router';
import { EProcessStatus } from 'core/dto';
import { distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { designerPaths } from '../../../designer.paths';

import { UserService } from '../../../global-services/user.service';
import { DsgDeepTranslationKey } from '../../../pipes/translate.pipe';
import { SidenavService } from '../sidenav.service';


export interface ISidenavTreeItem {
  text: DsgDeepTranslationKey,
  routeTo?: (string | EProcessStatus)[],
  icon?: string,
  children?: ISidenavTreeItem[],
  level?: number
  visible?: () => boolean
}


@Component({
  selector: 'ss-sidenav-tree',
  templateUrl: './sidenav-tree.component.html',
  styleUrls: ['./sidenav-tree.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SidenavTreeComponent {

  readonly #destroy = new Subject<void>()



  readonly sidenavTree: ISidenavTreeItem[] = [
    {
      text: 'ss-routes.documents',

      level: 0,
      children: [
        {
          text: 'ss-routes.in-progress',
          level: 1,
          children: [
            { text: 'ss-routes.for-me', routeTo: [designerPaths.processOverview, EProcessStatus.WaitingForMe] },
            {
              text: 'ss-routes.for-others', routeTo: [designerPaths.processOverview, EProcessStatus.WaitingForOthers],
              visible: () => this._userService.isCreator || this._userService.isAdmin
            },
            {
              text: 'ss-routes.for-all', routeTo: [designerPaths.processOverview, EProcessStatus.InProgress],
              visible: () => this._userService.isCreator || this._userService.isAdmin
            }
          ]
        },
        {
          text: 'ss-routes.finished',
          level: 1,
          routeTo: [designerPaths.processOverview, EProcessStatus.Finished],
          visible: () => this._userService.isCreator || this._userService.isAdmin
        },
        {
          text: 'ss-routes.rejected',
          level: 1,
          routeTo: [designerPaths.processOverview, EProcessStatus.Rejected],
          visible: () => this._userService.isCreator || this._userService.isAdmin
        },
        {
          text: 'ss-routes.expired',
          level: 1,
          routeTo: [designerPaths.processOverview, EProcessStatus.Expired],
          visible: () => this._userService.isCreator || this._userService.isAdmin
        },
        {
          text: 'ss-routes.all',
          level: 1,
          routeTo: [designerPaths.processOverview],
          visible: () => this._userService.isCreator || this._userService.isAdmin
        }
      ]
    },
    {
      text: 'ss-routes.drafts',
      level: 0,
      icon: 'square_foot',
      routeTo: [designerPaths.processOverview, EProcessStatus.Draft],
      visible: () => this._userService.isCreator || this._userService.isAdmin
    },
    {
      text: 'ss-routes.settings',
      visible: () => this._userService.isAdmin,
      level: 0,
      children: [
        {
          text: 'ss-routes.users',
          routeTo: [designerPaths.userManagement],
          level: 1,
        },
        {
          text: 'ss-routes.teams',
          routeTo: [designerPaths.teamManagement],
          level: 1,
        }
      ]
    },
  ]


  readonly dataSource = new MatTreeNestedDataSource<ISidenavTreeItem>()

  readonly treeControl = new NestedTreeControl<ISidenavTreeItem, string>(
    (node) => node.children,
    {
      trackBy: (node) => this.getTreeItemUrl(node) || node.text
    },
  )

  readonly expandedSubject = this._sidenavService.expandedSubject;


  readonly routeSubject = this._sidenavService.routeSubject;

  // readonly selectedRouteObs = this.routeSubject.pipe(
  //   map((route) => this.loopThroughTreeAndExpand(route))
  // )




  constructor(
    private readonly _router: Router,
    private readonly _sidenavService: SidenavService,
    private readonly _userService: UserService,
    private readonly _cdr: ChangeDetectorRef
  ) {

    this._userService.userSubject
      .pipe(
        distinctUntilChanged(),
        takeUntil(this.#destroy)
      )
      .subscribe(() => {
        const tree = this.filterVisible(this.sidenavTree)
        this.dataSource.data = tree;
        this.treeControl.dataNodes = tree;
      })

    // this.dataSource.data = this.sidenavTree


  }

  ngAfterViewInit() {
    this.loopThroughTreeAndExpand('')
    this._cdr.detectChanges();
    this.treeControl.expandAll();
  }


  filterVisible(items: ISidenavTreeItem[], level = 0): ISidenavTreeItem[] {

    if (!items) {
      return items;
    }

    return items
      .filter((item) => item.visible?.() !== false)
      .map((item) => {
        item.children = this.filterVisible(item.children, level + 1);
        item.level = level
        return item;
      })
  }

  loopThroughTreeAndExpand(route: string, sidenavTreeItems: ISidenavTreeItem[] = this.dataSource.data): ISidenavTreeItem {


    for (let item of sidenavTreeItems) {


      if (!item.children) {
        continue;
      }

      this.treeControl.expand(item);
      this.treeControl.expandDescendants(item);
      this.loopThroughTreeAndExpand(route, item.children);
    }

    return null;
  }

  getTreeItemUrl(treeItem: ISidenavTreeItem) {
    if (!treeItem.routeTo) {
      return;
    }

    return treeItem.routeTo.map((part) => '/' + part.toString()).join('');
  }

  handleReroute(treeItem: ISidenavTreeItem) {
    if (!treeItem.routeTo) {
      return;
    }
    this._sidenavService.handleReroute()
  }

  readonly hasChild = (_: any, treeItem: ISidenavTreeItem) => Boolean(treeItem.children)


  ngOnDestroy() {
    this.#destroy.next()
    this.#destroy.complete()
  }
}
